<template>
  <div class="layout-container">
    <!-- 左侧菜单 -->
    <div class="sidebar">
      <div class="logo">
        <i class="el-icon-cloudy"></i>
        <span class="logo-text">{{ t("message.system.title") }}</span>
      </div>
      <el-menu
        :default-active="activeMenu"
        class="sidebar-menu"
        @select="handleMenuSelect"
      >
        <el-menu-item index="device">
          <el-icon><Monitor /></el-icon>
          <span>{{ $t("message.menu.clientDevice") }}</span>
        </el-menu-item>
        <el-menu-item index="message">
          <el-icon><Message /></el-icon>
          <span>{{ $t("message.menu.message") }}</span>
        </el-menu-item>
        <el-menu-item index="account">
          <el-icon><User /></el-icon>
          <span>{{ $t("message.menu.account") }}</span>
        </el-menu-item>
      </el-menu>
    </div>

    <!-- 右侧内容区域 -->
    <div class="main-content">
      <div class="device-management-container">
        <el-card class="box-card">
          <template #header>
            <div class="card-header">
              <span>{{ $t("message.device.title") }}</span>
              <el-button type="primary" @click="handleAddDevice">
                {{ $t("message.device.add") }}
              </el-button>
            </div>
            <!-- Search Form -->
            <el-form :inline="true" :model="queryParams" class="search-form">
              <el-form-item :label="$t('message.device.company')">
                <el-tree-select
                  v-model="queryParams.companyId"
                  :data="companyTreeData"
                  :props="treeProps"
                  :placeholder="$t('message.device.selectCompany')"
                  check-strictly
                  clearable
                  filterable
                  style="width: 200px"
                  @change="handleSearch"
                />
              </el-form-item>

              <el-form-item :label="$t('message.device.name')">
                <el-input
                  v-model="queryParams.name"
                  :placeholder="$t('message.device.inputName')"
                  clearable
                  @change="handleSearch"
                  @keyup.enter="handleSearch"
                />
              </el-form-item>

              <el-form-item>
                <el-button type="primary" @click="handleSearch">
                  {{ $t("message.device.search") }}
                </el-button>
                <el-button @click="resetSearch">
                  {{ $t("message.device.reset") }}
                </el-button>
              </el-form-item>
            </el-form>
          </template>

          <!-- 状态标签页 -->
          <div class="status-tabs">
            <div 
              class="status-tab" 
              :class="{ active: activeStatus === 'all' }"
              @click="changeStatus('all')"
            >
              {{ $t('message.device.all') }}
            </div>
            <div 
              class="status-tab" 
              :class="{ active: activeStatus === 'online' }"
              @click="changeStatus('online')"
            >
              {{ $t('message.device.online') }}
            </div>
            <div 
              class="status-tab" 
              :class="{ active: activeStatus === 'offline' }"
              @click="changeStatus('offline')"
            >
              {{ $t('message.device.offline') }}
            </div>
          </div>

          <!-- 设备统计 -->
          <div class="device-stats">
            <div class="stat-item">
              <div class="stat-value">{{ deviceList.length }}</div>
              <div class="stat-label">{{ $t('message.device.total') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-value online">{{ onlineCount }}</div>
              <div class="stat-label">{{ $t('message.device.online') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-value offline">{{ offlineCount }}</div>
              <div class="stat-label">{{ $t('message.device.offline') }}</div>
            </div>
          </div>

          <el-table :data="filteredDeviceList" border style="width: 100%">
            <el-table-column
              prop="id"
              :label="$t('message.device.id')"
              width="80"
              sortable
            />
            <el-table-column
              prop="name"
              :label="$t('message.device.name')"
              width="150"
              sortable
            />
            <el-table-column
              prop="device_code"
              :label="$t('message.device.code')"
              width="150"
              sortable
            />
            <el-table-column
              prop="company_name"
              :label="$t('message.device.company')"
              width="150"
              sortable
            />
            <el-table-column
              :label="$t('message.device.state')"
              width="100"
              sortable
            >
              <template #default="scope">
                <el-tag :type="scope.row.state === 1 ? 'success' : 'danger'">
                  {{
                    scope.row.state === 1
                      ? $t("message.device.online")
                      : $t("message.device.offline")
                  }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="location"
              :label="$t('message.device.location')"
              width="150"
              sortable
            />
            <el-table-column
              prop="created_at"
              :label="$t('message.device.createTime')"
              width="180"
              sortable
            >
              <template #default="scope">
                {{ formatDate(scope.row.created_at) }}
              </template>
            </el-table-column>

            <el-table-column :label="$t('message.device.actions')" width="240">
              <template #default="scope">
                <el-button size="small" @click="handleViewDevice(scope.row)">
                  {{ $t("message.device.view") }}
                </el-button>
                <el-button size="small" @click="handleEditDevice(scope.row)">
                  {{ $t("message.device.edit") }}
                </el-button>
                <el-button
                  size="small"
                  type="danger"
                  @click="handleDeleteDevice(scope.row)"
                >
                  {{ $t("message.device.delete") }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="pagination">
            <el-pagination
              v-model:current-page="pagination.currentPage"
              v-model:page-size="pagination.pageSize"
              :total="pagination.total"
              :page-sizes="[10, 20, 30, 50]"
              layout="total, sizes, prev, pager, next, jumper"
              @current-change="handlePageChange"
              @size-change="handleSizeChange"
            />
          </div>
        </el-card>

        <!-- Device Edit Dialog -->
        <el-dialog
          v-model="dialogVisible"
          :title="dialogTitle"
          width="50%"
          :close-on-click-modal="false"
        >
          <device-form
            v-if="dialogVisible"
            ref="deviceFormRef"
            :device-data="currentDevice"
            @submit="handleSubmitDevice"
          />

          <template #footer>
            <el-button @click="dialogVisible = false">
              {{ $t("message.device.cancel") }}
            </el-button>
            <el-button type="primary" @click="submitDeviceForm">
              {{ $t("message.device.save") }}
            </el-button>
          </template>
        </el-dialog>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import { Monitor, Message, User } from '@element-plus/icons-vue';
import DeviceForm from "./DeviceForm.vue";
import {
  getDevices,
  deleteDevice,
  createDevice,
  updateDevice,
} from "@/api/DeviceManagement";
import { getCompanyTree } from "@/api/CompanyManager";

import { useRouter } from "vue-router";

const router = useRouter();
const { t } = useI18n();

// 左侧菜单
const activeMenu = ref('device');

// 状态筛选
const activeStatus = ref('all');

// 响应式数据
const deviceList = ref([]);
const currentDevice = ref({});
const dialogVisible = ref(false);
const dialogTitle = ref("");
const deviceFormRef = ref();
const companyTreeData = ref([]);

// 查询参数
const queryParams = reactive({
  companyId: null,
  name: "",
  pageNum: 1,
  pageSize: 10,
});

// 分页
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0,
});

const queryCompanyTreeParams = reactive({
  companyId: null,
});

const treeProps = {
  value: "id",
  label: "name",
  children: "children",
};

// 计算属性
const onlineCount = computed(() => {
  return deviceList.value.filter(device => device.state === 1).length;
});

const offlineCount = computed(() => {
  return deviceList.value.filter(device => device.state === 0).length;
});

const filteredDeviceList = computed(() => {
  if (activeStatus.value === 'all') {
    return deviceList.value;
  } else if (activeStatus.value === 'online') {
    return deviceList.value.filter(device => device.state === 1);
  } else {
    return deviceList.value.filter(device => device.state === 0);
  }
});

// 方法定义
const handleMenuSelect = (index) => {
  activeMenu.value = index;
  // 这里可以根据菜单项跳转到不同页面
  switch(index) {
    case 'device':
      // 已经在设备页面
      break;
    case 'message':
      ElMessage.info('跳转到消息页面');
      break;
    case 'account':
      ElMessage.info('跳转到账号页面');
      break;
  }
};

const changeStatus = (status) => {
  activeStatus.value = status;
};

const loadCompanyTree = async () => {
  try {
    const res = await getCompanyTree(queryCompanyTreeParams);
    companyTreeData.value = res.data.list;

    if (companyTreeData.value.length > 0 && !queryParams.companyId) {
      queryParams.companyId = companyTreeData.value[0].id;
    }
  } catch (error) {
    ElMessage.error(t("message.device.loadCompanyFailed"));
  }
};

const fetchDevices = async () => {
  try {
    const params = {
      companyId: queryParams.companyId,
      name: queryParams.name,
      pageNum: queryParams.pageNum,
      pageSize: queryParams.pageSize,
    };

    const res = await getDevices(params);
    if(!res.success) {
      ElMessage.error(res?.msg);  
      return;
    }
    deviceList.value = res.data.list;
    pagination.total = res.data.totalCount;
  } catch (error) {
    ElMessage.error(error.message || t("message.device.fetchError"));
  }
};

const handleSearch = () => {
  queryParams.pageNum = 1;
  if (!queryParams.companyId || queryParams.companyId <= 0) {
    ElMessage.warning(t("message.template.selectCompanyFirst"));
    return;
  }
  fetchDevices();
};

const resetSearch = () => {
  queryParams.name = "";
  queryParams.pageNum = 1;
  handleSearch();
};

const handleAddDevice = () => {
  currentDevice.value = {
    company_id: null,
    name: "",
    device_code: "",
    description: "",
    gateway_id: null,
    template_id: null,
    state: 1,
    location: "",
  };
  dialogTitle.value = t("message.device.addTitle");
  dialogVisible.value = true;
};

const handleViewDevice = (device) => {
  router.push(`realTimeData?deviceId=${device.id}&deviceName=${encodeURIComponent(device.name)}`);
};

const handleEditDevice = (device) => {
  currentDevice.value = { ...device };
  if (currentDevice.value.gateway_id === null || currentDevice.value.gateway_id === 0) {
    currentDevice.value.gateway_id = null;
  }
  dialogTitle.value = t("message.device.editTitle");
  dialogVisible.value = true;
};

const handleDeleteDevice = (device) => {
  ElMessageBox.confirm(
    t("message.device.deleteConfirm", { name: device.name }),
    t("message.common.tip"),
    {
      confirmButtonText: t("message.common.confirm"),
      cancelButtonText: t("message.common.cancel"),
      type: "warning",
    }
  )
    .then(async () => {
      try {
        const params = {
          id: device.id,
        };
        const retData = await deleteDevice(params);
        if (retData.success) {
          ElMessage.success(t("message.device.deleteSuccess"));
          fetchDevices();
        } else {
          ElMessage.error(retData?.msg || t("message.device.deleteFailed"));
        }
      } catch (error) {
        ElMessage.error(error.message || t("message.device.deleteFailed"));
      }
    })
    .catch(() => {
      ElMessage.info(t("message.common.deleteCancelled"));
    });
};

const submitDeviceForm = async () => {
  const ret = await deviceFormRef.value.submitForm();
  if (ret.success) {
    dialogVisible.value = false;
    fetchDevices();
  }
};

const handleSubmitDevice = async (formData) => {
  try {
    let ret;
    if (formData.id) {
      ret = await updateDevice(formData);
    } else {
      ret = await createDevice(formData);
    }

    if (ret.success) {
      ElMessage.success(
        t(formData.id ? "message.device.updateSuccess" : "message.device.addSuccess")
      );
    } else {
      ElMessage.error(ret?.msg || t("message.common.saveFailed"));
    }

    dialogVisible.value = false;
    fetchDevices();
  } catch (error) {
    ElMessage.error(error.message || t("message.device.submitError"));
  }
};

const handlePageChange = (val) => {
  queryParams.pageNum = val;
  fetchDevices();
};

const handleSizeChange = (val) => {
  queryParams.pageSize = val;
  fetchDevices();
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(/\//g, "-");
};

// 初始化
onMounted(async () => {
  await loadCompanyTree();
  await handleSearch();  
});
</script>

<style scoped>
.layout-container {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: 220px;
  background-color: #001529;
  color: white;
}

.logo {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #333;
}

.logo i {
  font-size: 24px;
  color: #1890ff;
  margin-right: 10px;
}

.logo-text {
  font-size: 18px;
  font-weight: bold;
  color: white;
}

.sidebar-menu {
  background-color: transparent;
  border: none;
}

.sidebar-menu .el-menu-item {
  color: #bfbfbf;
  height: 56px;
  line-height: 56px;
}

.sidebar-menu .el-menu-item.is-active {
  background-color: #1890ff;
  color: white;
}

.sidebar-menu .el-menu-item:hover {
  background-color: #1890ff;
  color: white;
}

.main-content {
  flex: 1;
  background-color: #f5f7fa;
  overflow: auto;
}

.device-management-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.search-form {
  margin-top: 15px;
}

.search-form :deep(.el-form-item) {
  margin-right: 15px;
  margin-bottom: 0;
}

/* 状态标签页样式 */
.status-tabs {
  display: flex;
  margin-bottom: 20px;
  background: white;
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.status-tab {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.status-tab.active {
  background: #1890ff;
  color: white;
}

.status-tab:hover:not(.active) {
  background: #f0f0f0;
}

/* 设备统计样式 */
.device-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-item {
  flex: 1;
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
}

.stat-value.online {
  color: #67c23a;
}

.stat-value.offline {
  color: #f56c6c;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .layout-container {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    height: auto;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .search-form :deep(.el-form-item) {
    margin-right: 0;
    margin-bottom: 10px;
    width: 100%;
  }

  .search-form :deep(.el-input),
  .search-form :deep(.el-tree-select) {
    width: 100%;
  }

  .device-stats {
    flex-direction: column;
  }
}
</style>